import { z } from 'zod';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import type {
  RoleAdminItem,
  PermissionAdminItem,
} from '@/features/admin-settings/types/rbac-admin';

// ─── Zod schemas for mutation inputs ──────────────────────────────────────────

export const RoleAdminSchema = z.object({
  type: z.string().min(1, DICTIONARY.adminSettingsPage.validation.typeRequired),
  scope: z.string().min(1, DICTIONARY.adminSettingsPage.validation.scopeRequired),
});
export type RoleAdminFormValues = z.infer<typeof RoleAdminSchema>;

export const SetRolePermissionsSchema = z.object({
  permissionIds: z.array(z.string()),
});
export type SetRolePermissionsPayload = z.infer<typeof SetRolePermissionsSchema>;

export const PermissionAdminSchema = z.object({
  name: z.string().min(1, DICTIONARY.adminSettingsPage.validation.nameRequired),
  description: z.string().nullable(),
});
export type PermissionAdminFormValues = z.infer<typeof PermissionAdminSchema>;

// ─── Fetchers ─────────────────────────────────────────────────────────────────

const fetchRoles = async (): Promise<RoleAdminItem[]> => {
  const { data } = await api.get<RoleAdminItem[]>(API_ROUTES.roles.list);
  return data;
};

const createRole = async (payload: RoleAdminFormValues): Promise<RoleAdminItem> => {
  const { data } = await api.post<RoleAdminItem>(API_ROUTES.roles.list, payload);
  return data;
};

const updateRole = async ({
  id,
  payload,
}: {
  id: string;
  payload: RoleAdminFormValues;
}): Promise<RoleAdminItem> => {
  const { data } = await api.put<RoleAdminItem>(API_ROUTES.roles.detail(id), payload);
  return data;
};

const deleteRole = async (id: string): Promise<void> => {
  await api.delete(API_ROUTES.roles.detail(id));
};

const setRolePermissions = async ({
  id,
  payload,
}: {
  id: string;
  payload: SetRolePermissionsPayload;
}): Promise<void> => {
  await api.put(API_ROUTES.roles.permissions(id), payload);
};

const fetchPermissions = async (): Promise<PermissionAdminItem[]> => {
  const { data } = await api.get<PermissionAdminItem[]>(API_ROUTES.permissions.list);
  return data;
};

const updatePermission = async ({
  id,
  payload,
}: {
  id: string;
  payload: PermissionAdminFormValues;
}): Promise<PermissionAdminItem> => {
  const { data } = await api.patch<PermissionAdminItem>(API_ROUTES.permissions.detail(id), payload);
  return data;
};

// ─── Query options factories ───────────────────────────────────────────────────

export const getRolesQueryOptions = () =>
  queryOptions({ queryKey: QUERY_KEYS.roles.list, queryFn: fetchRoles });

export const getPermissionsQueryOptions = () =>
  queryOptions({ queryKey: QUERY_KEYS.permissions.list, queryFn: fetchPermissions });

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useRoles = () => useQuery(getRolesQueryOptions());

export const usePermissions = () => useQuery(getPermissionsQueryOptions());

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.list });
      toaster.create({ type: 'success', title: DICTIONARY.adminSettingsPage.toast.roleCreated });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.roleCreateError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateRole,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.list });
      toaster.create({ type: 'success', title: DICTIONARY.adminSettingsPage.toast.roleUpdated });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.roleUpdateError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.list });
      toaster.create({ type: 'success', title: DICTIONARY.adminSettingsPage.toast.roleDeleted });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.roleDeleteError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useSetRolePermissions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setRolePermissions,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.list });
      toaster.create({
        type: 'success',
        title: DICTIONARY.adminSettingsPage.toast.rolePermissionsSaved,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.rolePermissionsError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useUpdatePermission = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePermission,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.permissions.list });
      toaster.create({
        type: 'success',
        title: DICTIONARY.adminSettingsPage.toast.permissionUpdated,
      });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.permissionUpdateError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
